import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { serializeFilters, serializeSort } from '../utils/filter-serializer';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import type { TaskFilters, TaskSummaryDto } from '../types';

interface UseTimelineTasksParams {
  projectId: string;
  boardId?: string;
  filters: TaskFilters;
  showCompleted: boolean;
}

interface UseTimelineTasksResult {
  tasks: TaskSummaryDto[];
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
}

/**
 * Fetches the tasks that back the Timeline's per-person swimlanes. Same rich
 * rows and pagination pattern as the existing Gantt (`useGanttModel`) — no
 * `dateStart`/`dateEnd` narrowing, per the backend integration guide ("task
 * bars are already covered ... unchanged, no new params needed"): the visible
 * window is a purely client-side clip, not a server filter.
 */
export function useTimelineTasks({
  projectId,
  boardId,
  filters,
  showCompleted,
}: UseTimelineTasksParams): UseTimelineTasksResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const serializedFilters = serializeFilters(filters);

  const {
    data: rawTasks,
    isLoading,
    isError,
  } = useQuery({
    queryKey: TASK_KEYS.timelineTasks(
      orgId ?? '',
      projectId,
      boardId,
      serializedFilters,
      filters.search,
    ),
    queryFn: async ({ signal }): Promise<TaskSummaryDto[]> => {
      const all: TaskSummaryDto[] = [];
      let cursor: string | undefined = undefined;
      for (let page = 0; page < 100; page++) {
        const result = await tasksService.getQuery(
          projectId,
          {
            view: 'gantt',
            limit: 100,
            sort: serializeSort({ columnId: 'startDate', direction: 'asc' }),
            ...(boardId && { boardId }),
            ...(serializedFilters && { filters: serializedFilters }),
            ...(filters.search && { search: filters.search }),
            ...(cursor && { cursor }),
          },
          signal,
        );
        all.push(...(result.items ?? []));
        if (!result.nextCursor) return all;
        cursor = result.nextCursor;
      }
      return all; // safety bound hit
    },
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId,
  });

  const allTasks = rawTasks ?? [];
  const tasks = showCompleted ? allTasks : allTasks.filter((task) => !task.isCompleted);

  return {
    tasks,
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && tasks.length === 0,
  };
}
